import { ComponentTypes } from '../../Data/Components';
import getUsStateCodeFromPostcode from '../../Helpers/usState';
import { getPostcodeComponent } from '../__helpers__/component.helpers';

const SUBMIT_BUTTON_TEXT_REPLACEMENT_REGEX = /\[submit_button_text\]/g;
const LEGAL_STATEMENT_REPLACEMENT_REGEX = /\[exclusive_supplier_name\]/g;

/**
 * For non US forms we need to update the privacy policy every
 * time a new country code is selected in the phone number input.
 */
export const getLocaleForPrivacyPolicy = (
  phoneNumberCountryCode,
  countryCode,
  languageCode,
  localeCode
) => {
  return countryCode === 'US'
    ? localeCode
    : `${languageCode}_${phoneNumberCountryCode}`;
};

/**
 * The customer's US state (e.g. 'NY'), derived from the postcode that ends up
 * on the lead. Read from the store rather than set by the postcode field, so it
 * holds however the postcode was filled in (typed, prefilled, address lookup).
 * Returns null on non-US forms and for postcodes outside a known state.
 */
export const selectCustomerStateCode = (store) => {
  if (store.formConfig.countryCode !== 'US') return null;

  const postcode = getPostcodeComponent({ components: store.components })?.input
    ?.value;

  return getUsStateCodeFromPostcode(postcode);
};

/**
 * Uses a state override only when it comes with its consent hash, so the hash
 * recorded on the lead always matches the text the customer saw.
 */
const overrideOrStandard = ({
  overrideStatement,
  overrideHash,
  standardStatement,
  standardHash,
}) =>
  overrideStatement && overrideHash
    ? { statement: overrideStatement, hash: overrideHash }
    : { statement: standardStatement, hash: standardHash };

const getStateAwareSupplierLegalStatements = ({
  supplierLegalStatements,
  stateSupplierLegalStatements,
}) => {
  const standard = supplierLegalStatements ?? {};
  if (!stateSupplierLegalStatements) return standard;

  const single = overrideOrStandard({
    overrideStatement: stateSupplierLegalStatements.singleSupplierStatement,
    overrideHash:
      stateSupplierLegalStatements.singleSupplierConsentedLegalStatementHash,
    standardStatement: standard.singleSupplierStatement,
    standardHash: standard.singleSupplierConsentedLegalStatementHash,
  });
  const multiple = overrideOrStandard({
    overrideStatement: stateSupplierLegalStatements.multipleSupplierStatement,
    overrideHash:
      stateSupplierLegalStatements.multipleSupplierConsentedLegalStatementHash,
    standardStatement: standard.multipleSupplierStatement,
    standardHash: standard.multipleSupplierConsentedLegalStatementHash,
  });

  return {
    ...standard,
    singleSupplierStatement: single.statement,
    singleSupplierConsentedLegalStatementHash: single.hash,
    multipleSupplierStatement: multiple.statement,
    multipleSupplierConsentedLegalStatementHash: multiple.hash,
  };
};

/**
 * Swaps the standard statement, its hash and the supplier (TCPA) statements
 * for the customer's state-specific variants (e.g. NY) where the gateway
 * served one. Anything the state does not override falls back to the standard.
 * A missing standard statement stays missing so a failed request still blocks submission.
 *
 * `stateLegalStatements` is served by chameleon-gateway as
 * `[{ stateCode, statement, consentedLegalStatementHash, supplierLegalStatements }]`,
 * one entry per state that has any override; null fields mean no override.
 *
 * WARNING: state codes are matched case-insensitively on purpose. Eagle keys
 * its overrides by state code and the gateway's response key conversion has
 * lowercased such keys before ("NY" arriving as "ny"). The gateway uppercases
 * them, but never rely on that here: a case mismatch would silently show a
 * New York customer the wrong consent text.
 */
export const getStateAwareLegalStatement = ({
  legalStatement,
  customerStateCode,
}) => {
  const {
    statement,
    consentedLegalStatementHash,
    supplierLegalStatements,
    stateLegalStatements,
  } = legalStatement;
  const standardLegalStatement = {
    statement,
    consentedLegalStatementHash,
    supplierLegalStatements,
  };

  if (!statement || !customerStateCode) return standardLegalStatement;
  if (!Array.isArray(stateLegalStatements)) return standardLegalStatement;

  const stateLegalStatement = stateLegalStatements.find(
    (candidate) =>
      candidate.stateCode?.toUpperCase() === customerStateCode.toUpperCase()
  );
  if (!stateLegalStatement) return standardLegalStatement;

  const main = overrideOrStandard({
    overrideStatement: stateLegalStatement.statement,
    overrideHash: stateLegalStatement.consentedLegalStatementHash,
    standardStatement: statement,
    standardHash: consentedLegalStatementHash,
  });

  return {
    statement: main.statement,
    consentedLegalStatementHash: main.hash,
    supplierLegalStatements: getStateAwareSupplierLegalStatements({
      supplierLegalStatements,
      stateSupplierLegalStatements: stateLegalStatement.supplierLegalStatements,
    }),
  };
};

export const isLegalStatementValid = ({
  statement,
  supplierId,
  singleSupplierStatement,
  multipleSupplierMatchStatement,
  components,
  currentPage,
  submitButtonText,
  customerMatches,
}) => {
  const statementShown = getLegalStatementToShow({
    statement,
    supplierId,
    singleSupplierStatement,
    multipleSupplierMatchStatement,
    components,
    currentPage,
    submitButtonText,
    customerMatches,
  });
  return typeof statementShown === 'string' && statementShown !== '';
};

export const shouldLegalStatementBeVisible = ({
  isDynamicHeightEnabled,
  hasSupplierMatchQuestion,
  shouldRenderLegalStatement,
  isConsentStatementAboveNavigation,
  hasStatementGraduallyDisappeared,
  isFinalPageBeforeSubmission,
  isPartialSubmissionPage,
  isSelectAndConnectPage,
  isBsaPhonePage,
}) => {
  // BSA phone page always shows the legal statement regardless of other conditions
  if (isBsaPhonePage) {
    return true;
  }

  /*
   * If dynamic height is enabled the dynamic height logic will determine
   * whether the legal statement should be visible or not. It then is the
   * <LegalStatement /> component's responsibility to then render that
   * statement depending on additional separate state value checks.
   */
  if (isDynamicHeightEnabled) {
    return (
      shouldRenderLegalStatement &&
      (!isConsentStatementAboveNavigation ||
        (isConsentStatementAboveNavigation &&
          !hasStatementGraduallyDisappeared))
    );
  }

  /*
   * If dynamic height is not enabled, we only need to check for the following conditions:
   */
  return Boolean(
    hasSupplierMatchQuestion ||
      isFinalPageBeforeSubmission ||
      isSelectAndConnectPage ||
      isPartialSubmissionPage
  );
};

export const currentPageHasSelectAndConnectComponents = (
  components,
  currentPage
) => {
  return Object.values(components).some(
    (component) =>
      component.component.page === currentPage &&
      component.component.type === ComponentTypes.SELECT_AND_CONNECT
  );
};

/**
 * formatSingleSupplierStatement
 *
 * Supplier legal statements will be supplied with a placeholder for the suppliers who have been matched to the customer. This function replaces the placeholder with the appropriate supplier name. If there is no supplier name or the statement has not yet been obtained it will return undefined.
 */
const formatSingleSupplierStatement = ({
  predefinedBrandLabel,
  singleSupplierStatement,
  submitButtonText,
}) => {
  if (typeof singleSupplierStatement !== 'string' || !predefinedBrandLabel) {
    return;
  }

  let statement = singleSupplierStatement.replace(
    LEGAL_STATEMENT_REPLACEMENT_REGEX,
    predefinedBrandLabel
  );

  statement = replaceSubmitButtonTextPlaceholder({
    statement,
    submitButtonText,
  });

  return statement;
};

/**
 * replaceSubmitButtonTextPlaceholder
 *
 * Replaces the submit button text placeholder in any legal statement
 */
const replaceSubmitButtonTextPlaceholder = ({
  statement,
  submitButtonText,
}) => {
  if (typeof statement !== 'string') {
    return;
  }

  return statement.replace(
    SUBMIT_BUTTON_TEXT_REPLACEMENT_REGEX,
    submitButtonText
  );
};

export const isPredefinedSupplierBrandAvailable = ({
  supplierId,
  customerMatches,
}) => {
  return (
    supplierId && Array.isArray(customerMatches) && customerMatches.length === 1
  );
};

/**
 * True when we should show the single-supplier statement (and use its hash):
 * either predefined supplier with one match, or one match and that brand is
 * configured to use a single-supplier statement when alone.
 */
export const shouldShowSingleSupplierStatement = ({
  supplierId,
  customerMatches,
}) => {
  // Predefined supplier + one match is always a single-supplier case.
  if (isPredefinedSupplierBrandAvailable({ supplierId, customerMatches })) {
    return true;
  }

  if (!Array.isArray(customerMatches) || customerMatches.length !== 1) {
    return false;
  }
  const [match] = customerMatches;

  return Boolean(match?.requiresSingleSupplierStatementWhenAlone);
};

/**
 * getLegalStatementToShow
 *
 * Decides whether the normal and supplier legal statement should be shown and formats the statement with suppliers if required.
 */
export const getLegalStatementToShow = ({
  components,
  supplierId,
  singleSupplierStatement,
  customerMatches,
  multipleSupplierMatchStatement,
  statement,
  currentPage,
  statementOverride,
  submitButtonText,
}) => {
  if (statementOverride) {
    return statementOverride;
  }

  if (currentPageHasSelectAndConnectComponents(components, currentPage)) {
    return replaceSubmitButtonTextPlaceholder({
      statement: multipleSupplierMatchStatement,
      submitButtonText,
    });
  }

  if (shouldShowSingleSupplierStatement({ supplierId, customerMatches })) {
    const predefinedBrandLabel = customerMatches[0].brandLabel;
    return formatSingleSupplierStatement({
      predefinedBrandLabel,
      singleSupplierStatement,
      submitButtonText,
    });
  }

  return replaceSubmitButtonTextPlaceholder({
    statement,
    submitButtonText,
  });
};

export const getLegalStatementToShowOnResultsPage = ({
  supplierId,
  singleSupplierStatement,
  customerMatches,
  statement,
  statementOverride,
}) => {
  if (statementOverride) {
    return statementOverride;
  }

  if (shouldShowSingleSupplierStatement({ supplierId, customerMatches })) {
    const predefinedBrandLabel = customerMatches[0].brandLabel;
    return formatSingleSupplierStatement({
      predefinedBrandLabel,
      singleSupplierStatement,
      submitButtonText: '[submit_button_text]', // just keep the placeholder for submit button text, we'll replace in the results page
    });
  }

  return statement || '';
};
